import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Tv, X, GripVertical, ShieldAlert, ArrowUpDown, Search, Sun, Moon, Play, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import EpisodePicker from '../components/EpisodePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 30000;

const SERVICE_META = {
  netflix:        { name: 'Netflix',       color: '#E50914' },
  hulu:           { name: 'Hulu',          color: '#1CE783' },
  disney_plus:    { name: 'Disney+',       color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',       color: '#B535F6' },
  amazon_prime:   { name: 'Amazon Prime',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',     color: '#555' },
  peacock:        { name: 'Peacock',       color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',    color: '#0064FF' },
};

const STATUS_CYCLE = ['watching', 'watched', 'want_to_watch'];

// Same "bad stored link" shape backend/server.py's _is_bad_stored_deeplink
// now checks (extended 2026-09-23 to also catch search-URL results, which
// is what get_streaming_deep_link produced for EVERY service for months
// while the old RapidAPI integration it depended on was silently dead --
// see that function's own comment). This list was narrower than the
// backend's own and never caught that shape, so a stale cached search
// link on a show would get reused here forever even after the backend
// fix shipped. Kept in sync with the backend's check.
const BAD_DEEPLINK_PATTERNS = ['justwatch.com', 'themoviedb.org', 'tmdb.org', '/search?q=', '/s?k=', '/search/'];
function isBadDeepLink(url) {
  if (!url) return true;
  const lower = url.toLowerCase();
  return BAD_DEEPLINK_PATTERNS.some(p => lower.includes(p));
}

function getDeepLink(serviceId, title) {
  const q = encodeURIComponent(title);
  switch (serviceId) {
    case 'netflix':        return `https://www.netflix.com/search?q=${q}`;
    case 'hulu':           return `https://www.hulu.com/search?q=${q}`;
    case 'disney_plus':    return `https://www.disneyplus.com/search/${q}`;
    case 'hbo_max':        return `https://www.max.com/search?q=${q}`;
    case 'amazon_prime':   return `https://www.amazon.com/s?k=${q}&i=instant-video`;
    case 'apple_tv':       return `https://tv.apple.com/search?term=${q}`;
    case 'peacock':        return `https://www.peacocktv.com/search?q=${q}`;
    case 'paramount_plus': return `https://www.paramountplus.com/search/${q}`;
    default:               return '';
  }
}

const STATUS_OPTIONS = [
  { value: 'watching',      label: 'Watching' },
  { value: 'watched',       label: 'Watched' },
  { value: 'want_to_watch', label: 'Start Watching' },
];

function getStatusBadge(status) {
  switch (status) {
    case 'want_to_watch': return { label: 'Start',    classes: 'bg-violet-500/20 text-violet-400 border-violet-500/30' };
    case 'watching':      return { label: 'Continue', classes: 'bg-[#4A9EFF]/20 text-[#4A9EFF] border-[#4A9EFF]/30' };
    case 'watched':       return { label: 'Finished', classes: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
    default:              return null;
  }
}

export default function Watchlist() {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [connectedServices, setConnectedServices] = useState([]);
  const [folders, setFolders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterService, setFilterService] = useState('all');
  // Watchlist = what you WANT to watch; what you're watching is on the Hub.
  const [filterStatus, setFilterStatus] = useState('want_to_watch');
  const [form, setForm] = useState({ title: '', service_id: '', image_url: '', description: '', folder_id: '', deep_link: '', tmdb_id: '', media_type: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [episodeBadges, setEpisodeBadges] = useState({});
  const [finishRates, setFinishRates] = useState({});
  const [spoilerShields, setSpoilerShields] = useState({});
  const [sortBy, setSortBy] = useState('rank');
  const [reorderMode, setReorderMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [activeSessionShow, setActiveSessionShow] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [watchlistSearch, setWatchlistSearch] = useState('');
  // Manual "where am I in this show" -- { show, season, episode } while open.
  const [positioning, setPositioning] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('qude_theme') !== 'light';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('light-mode', !darkMode);
    localStorage.setItem('qude_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const touchDragIndex = useRef(null);
  const debounceRef = useRef(null);
  const pollRef = useRef(null);

  const fetchEpisodeBadges = useCallback(async (showList, authToken) => {
    if (!showList || showList.length === 0) return;
    const headers = { Authorization: 'Bearer ' + authToken };
    const badges = {};
    const rates = {};
    const shields = {};

    await Promise.all(
      showList.filter(s => s.tmdb_id && s.media_type !== 'movie').map(async (show) => {
        try {
          const res = await axios.get(`${API}/tmdb/next-episode/${show.tmdb_id}`, { headers });
          if (res.data.next_episode) badges[show.show_id] = res.data.next_episode;
        } catch (err) {}
      })
    );
    setEpisodeBadges(badges);

    await Promise.all(
      showList.filter(s => s.tmdb_id).map(async (show) => {
        try {
          const res = await axios.get(`${API}/shows/finish-rate/${show.tmdb_id}`);
          if (res.data.finish_rate !== null) rates[show.tmdb_id] = res.data.finish_rate;
        } catch (err) {}
      })
    );
    setFinishRates(rates);

    await Promise.all(
      showList.filter(s => s.tmdb_id && s.media_type !== 'movie' && s.status === 'watching').map(async (show) => {
        try {
          const res = await axios.get(`${API}/spoiler-shield/${show.tmdb_id}`, { headers });
          shields[show.show_id] = res.data;
        } catch (err) {}
      })
    );
    setSpoilerShields(shields);
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (document.visibilityState === 'hidden') return;
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [showsRes, svcRes, foldersRes] = await Promise.all([
        axios.get(`${API}/shows`, { headers }),
        axios.get(`${API}/services/connected`, { headers }),
        axios.get(`${API}/folders`, { headers }),
      ]);
      const prevCount = shows.length;
      setShows(showsRes.data);
      setConnectedServices(svcRes.data);
      setFolders(foldersRes.data);
      if (!silent || showsRes.data.length !== prevCount) {
        fetchEpisodeBadges(showsRes.data, token);
      }
      if (silent) setLastSynced(new Date());
    } catch (err) {}
  }, [getToken, fetchEpisodeBadges, shows.length]);

  useEffect(() => { if (user) fetchData(false); }, [user]); // eslint-disable-line

  // Background self-heal for stale/bad deep_link values -- GET /api/shows
  // just returns whatever's stored on each show doc, and for months
  // get_streaming_deep_link (the resolver behind it) only ever produced
  // search-URL results (see server.py's fix comment), so most existing
  // shows' cached deep_link is a search page, not a real one. handleWatchTap
  // must stay fully synchronous for the Android WebView handoff (see its own
  // comment), so this refreshes bad links quietly in the background instead.
  //
  // Deliberately calls /api/launch-url-v2 (season/episode-aware, always
  // resolves fresh, same endpoint every other Play button in this app
  // uses) rather than /api/launch-url/{show_id} -- that older endpoint
  // both drops season/episode entirely AND returns a good-looking CACHED
  // link without ever re-checking whether the user has since moved to a
  // later episode, which would satisfy the "not a search page" bar but
  // still fail "resumes where I actually am."
  const healedShowIds = useRef(new Set());
  useEffect(() => {
    if (shows.length === 0) return;
    const toHeal = shows.filter(
      s => s.service_id && !healedShowIds.current.has(s.show_id) && isBadDeepLink(s.deep_link)
    );
    if (toHeal.length === 0) return;
    (async () => {
      const token = await getToken().catch(() => null);
      if (!token) return;
      for (const show of toHeal) {
        healedShowIds.current.add(show.show_id);
        try {
          const params = new URLSearchParams({
            service_id: show.service_id || '',
            media_type: show.media_type || 'tv',
            title: show.title || '',
            ...(show.tmdb_id ? { tmdb_id: show.tmdb_id } : {}),
            ...(show.current_season ? { season: show.current_season } : {}),
            ...(show.current_episode ? { episode: show.current_episode } : {}),
          });
          const res = await axios.get(`${API}/launch-url-v2?${params}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const freshUrl = res.data?.url;
          if (freshUrl && !isBadDeepLink(freshUrl)) {
            setShows(prev => prev.map(s => (s.show_id === show.show_id ? { ...s, deep_link: freshUrl } : s)));
          }
        } catch {}
      }
    })();
  }, [shows, getToken]);

  useEffect(() => {
    if (!user) return;
    pollRef.current = setInterval(() => fetchData(true), POLL_INTERVAL);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchData(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, fetchData]);

  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/watch-sessions/latest`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.session) {
          setActiveSession(res.data.session);
          setActiveSessionShow(res.data.show);
        }
      } catch (err) {}
    };
    if (user) checkActiveSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const doSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/tmdb/search`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearchResults(res.data.results || []);
    } catch (err) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [getToken]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const selectTMDBResult = async (result) => {
    setSelectedResult(result);
    setSearchResults([]);
    setSearchQuery('');
    setProviders([]);
    setForm(prev => ({ ...prev, title: result.title, image_url: result.poster_url || '', description: '', service_id: '', deep_link: '', tmdb_id: String(result.tmdb_id || ''), media_type: result.media_type || 'tv' }));
    try {
      setLoadingProviders(true);
      const token = await getToken();
      const mediaType = result.media_type === 'movie' ? 'movie' : 'tv';
      const res = await axios.get(`${API}/tmdb/providers/${result.tmdb_id}`, {
        params: { media_type: mediaType },
        headers: { Authorization: `Bearer ${token}` },
      });
      const providerList = res.data.services || [];
      const justwatchLink = res.data.justwatch_link || '';
      setProviders(providerList);
      const available = providerList.find(p => p.available);
      if (available) {
        setForm(prev => ({
          ...prev,
          service_id: available.service_id,
          deep_link: justwatchLink || getDeepLink(available.service_id, result.title),
        }));
      } else if (justwatchLink) {
        setForm(prev => ({ ...prev, deep_link: justwatchLink }));
      }
    } catch (err) {}
    finally { setLoadingProviders(false); }
  };

  const handleServiceChange = (serviceId) => {
    setForm(prev => ({ ...prev, service_id: serviceId, deep_link: prev.deep_link || (prev.title ? getDeepLink(serviceId, prev.title) : '') }));
  };

  const clearSelection = () => {
    setSelectedResult(null);
    setProviders([]);
    setForm(prev => ({ ...prev, title: '', image_url: '', description: '', service_id: '', deep_link: '' }));
  };

  // ── FIX 1: Duplicate prevention ──────────────────────────────────────────
  const addShow = async () => {
    if (!form.title || !form.service_id) { toast.error('Title and service are required'); return; }

    // Check for duplicate — same title + same service
    const duplicate = shows.find(
      s => s.title.toLowerCase() === form.title.toLowerCase() &&
           s.service_id === form.service_id
    );
    if (duplicate) {
      toast.error(`${form.title} is already in your watchlist`);
      return;
    }

    try {
      const token = await getToken();
      await axios.post(`${API}/shows`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Show added!');
      setForm({ title: '', service_id: '', image_url: '', description: '', folder_id: '', deep_link: '', tmdb_id: '', media_type: '' });
      setSelectedResult(null);
      setProviders([]);
      setDialogOpen(false);
      fetchData(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add show');
    }
  };

  const openPositioner = (show) => {
    setPositioning({
      show,
      season: Number(show.current_season) > 0 ? Number(show.current_season) : 1,
      episode: Number(show.current_episode) > 0 ? Number(show.current_episode) : 1,
    });
  };

  // track/set writes viewing_state (what Hub reads) and mirrors season/
  // episode + "watching" back onto this shows row. title/tmdb_id/media_type
  // seed a show that has no viewing_state row yet. show_key must match
  // backend reconciliation.show_key().
  const savePosition = async () => {
    if (!positioning) return;
    const { show, season, episode } = positioning;
    const mt = (show.media_type || 'tv').toLowerCase();
    const showKey = show.tmdb_id ? `tmdb:${mt}:${show.tmdb_id}` : `title:${mt}:${show.title.trim().toLowerCase()}`;
    try {
      const token = await getToken();
      await axios.post(`${API}/track/set`,
        { show_key: showKey, season, episode, status: 'watching', title: show.title, tmdb_id: show.tmdb_id || '', media_type: mt },
        { headers: { Authorization: `Bearer ${token}` } });
      setShows(prev => prev.map(s => s.show_id === show.show_id
        ? { ...s, current_season: season, current_episode: episode, status: 'watching' } : s));
      toast.success(`Updated to S${season}E${episode}`);
      setPositioning(null);
    } catch { toast.error('Could not update'); }
  };

  const deleteShow = async (showId) => {
    try {
      const token = await getToken();
      await axios.delete(`${API}/shows/${showId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Show removed');
      fetchData(false);
    } catch (err) { toast.error('Failed to delete show'); }
  };

  const cycleStatus = async (e, show) => {
    e.stopPropagation();
    const currentIdx = STATUS_CYCLE.indexOf(show.status);
    const nextStatus = STATUS_CYCLE[(currentIdx + 1) % STATUS_CYCLE.length];
    const labels = { watching: 'Watching', watched: 'Finished ✓', want_to_watch: 'In Queue' };

    setShows(prev => prev.map(s => s.show_id === show.show_id ? { ...s, status: nextStatus } : s));
    setUpdatingStatus(show.show_id);

    try {
      const token = await getToken();
      await axios.put(`${API}/shows/${show.show_id}`, { status: nextStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${show.title} → ${labels[nextStatus]}`);
    } catch (err) {
      setShows(prev => prev.map(s => s.show_id === show.show_id ? { ...s, status: show.status } : s));
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const saveReorder = async (newShows) => {
    try {
      const token = await getToken();
      await axios.put(`${API}/shows/reorder`, { show_ids: newShows.map(s => s.show_id) }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) { fetchData(false); }
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    const newShows = [...shows];
    const [moved] = newShows.splice(dragIndex, 1);
    newShows.splice(idx, 0, moved);
    setShows(newShows);
    setDragIndex(idx);
  };
  const handleDragEnd = () => { setDragIndex(null); saveReorder(shows); };

  const handleTouchStart = (e, idx) => { if (!reorderMode) return; touchDragIndex.current = idx; };
  const handleTouchMove = (e) => {
    if (!reorderMode || touchDragIndex.current === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const card = el?.closest('[data-show-index]');
    if (card) {
      const targetIdx = parseInt(card.getAttribute('data-show-index'));
      if (!isNaN(targetIdx) && targetIdx !== touchDragIndex.current) {
        const newShows = [...shows];
        const [moved] = newShows.splice(touchDragIndex.current, 1);
        newShows.splice(targetIdx, 0, moved);
        setShows(newShows);
        touchDragIndex.current = targetIdx;
      }
    }
  };
  const handleTouchEnd = () => { if (!reorderMode) return; saveReorder(shows); touchDragIndex.current = null; };

  const handleWatchTap = (show) => {
    if (reorderMode) return;

    // URL must be resolved synchronously before window.open — this is the regression fix.
    //
    // The broken path (introduced in cec505c): window.open('about:blank','_blank') then
    // popup.location.href = url after an async API call. In the Android WebView the blank
    // popup window does NOT inherit the WebViewClient's shouldOverrideUrlLoading, so
    // netflix.com loads as a website (redirects to login/home) instead of opening the app.
    //
    // The working path: window.open(url, '_blank') synchronously within the tap event.
    // Android's WebView fires shouldOverrideUrlLoading for the real URL and hands it off
    // to the Netflix / Prime / etc. app correctly.
    const storedOk = !isBadDeepLink(show.deep_link);
    const url = storedOk
      ? show.deep_link
      : (show.service_id && show.title
          ? getDeepLink(show.service_id, show.title)
          : `https://www.google.com/search?q=${encodeURIComponent(show.title || '')}`);

    if (url) {
      // On Android, use the native bridge directly — window.open('_blank') in WebView
      // with setSupportMultipleWindows=false may not fire shouldOverrideUrlLoading,
      // causing the URL to load in-page as a website instead of opening the native app.
      if (window.QudeAndroid && window.QudeAndroid.openStreamingUrl) {
        window.QudeAndroid.openStreamingUrl(url);
      } else {
        window.open(url, '_blank');
      }
    }

    // Session logging in background — does not affect the launch
    getToken().then(token => {
      axios.post(`${API}/watch-sessions`,
        { show_id: show.show_id, tmdb_id: show.tmdb_id, service_id: show.service_id },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
    }).catch(() => {});
  };

  const dismissSession = async (markWatched = false) => {
    try {
      const token = await getToken();
      await axios.post(`${API}/watch-sessions/${activeSession.session_id}/return`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (markWatched) {
        await axios.put(`${API}/shows/${activeSession.show_id}`, { status: 'watched' }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchData(false);
      }
    } catch (err) {}
    setActiveSession(null);
    setActiveSessionShow(null);
  };

  const continueWatching = shows
    .filter(s => s.status === 'watching' && s.last_watched_at)
    .sort((a, b) => new Date(b.last_watched_at) - new Date(a.last_watched_at))
    .filter((s, i, arr) => !s.tmdb_id || arr.findIndex(x => x.tmdb_id === s.tmdb_id) === i)
    .slice(0, 6);

  const filteredShows = shows
    .filter(s => {
      if (filterService !== 'all' && s.service_id !== filterService) return false;
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (watchlistSearch.trim()) {
        return s.title.toLowerCase().includes(watchlistSearch.toLowerCase());
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      if (sortBy === 'date') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'rating') return (b.user_rating || 0) - (a.user_rating || 0);
      if (sortBy === 'watched') return new Date(b.last_watched_at || 0) - new Date(a.last_watched_at || 0);
      return (a.rank || 999) - (b.rank || 999); // default: manual rank
    });

  const resetDialog = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedResult(null);
    setProviders([]);
    setForm({ title: '', service_id: '', image_url: '', description: '', folder_id: '', deep_link: '', tmdb_id: '', media_type: '' });
  };

  const cardBg = darkMode ? 'bg-[#0A0A0A]' : 'bg-white';
  const cardBorder = darkMode ? 'border-white/5 hover:border-white/10' : 'border-neutral-200 hover:border-neutral-300';
  const textPrimary = darkMode ? 'text-white' : 'text-neutral-900';
  const textSecondary = darkMode ? 'text-neutral-500' : 'text-neutral-500';
  const inputBg = darkMode ? 'bg-black/20 border-white/10 text-white placeholder:text-neutral-600' : 'bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400';

  return (
    // FIX 3: pb-24 ensures content clears phone navigation bar
    <div className="max-w-5xl mx-auto space-y-6 pb-24" data-testid="watchlist-page">

      {/* Active session banner */}
      {activeSession && activeSessionShow && !['finished', 'watched'].includes(
        shows.find(s => s.show_id === activeSession.show_id)?.status ?? activeSessionShow.status
      ) && (
        <div className="flex items-center justify-between p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
          <div className="flex items-center gap-3">
            {activeSessionShow.image_url && (
              <img src={activeSessionShow.image_url} alt={activeSessionShow.title} className="w-10 h-14 rounded-lg object-cover flex-shrink-0" />
            )}
            <div>
              <p className={`text-sm font-semibold ${textPrimary}`}>Welcome back!</p>
              <p className="text-xs text-neutral-400">Still watching <span className="text-violet-400">{activeSessionShow.title}</span>?</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => dismissSession(true)} className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">Finished ✓</button>
            <button onClick={() => dismissSession(false)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-neutral-400 border border-white/10 font-semibold">Still watching</button>
          </div>
        </div>
      )}

      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={`text-3xl sm:text-4xl font-black tracking-tight ${textPrimary}`} style={{ fontFamily: 'Outfit' }}>Watchlist</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className={textSecondary}>{shows.length} shows tracked</p>
            {lastSynced && (
              <span className="text-xs text-neutral-600">
                · synced {lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDarkMode(d => !d)}
            className="p-2 rounded-full border border-white/10 text-neutral-400 hover:text-white transition-colors"
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {shows.length > 1 && (
            <Button
              onClick={() => {
                const next = !reorderMode;
                setReorderMode(next);
                if (next) toast('Drag the ≡ handles to reorder', { duration: 2500 });
              }}
              variant="outline"
              className={`rounded-full text-sm gap-2 transition-all ${
                reorderMode
                  ? 'border-violet-500/50 text-violet-400 bg-violet-500/10'
                  : 'border-white/10 text-neutral-400'
              }`}
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {reorderMode ? 'Done' : 'Reorder'}
            </Button>
          )}

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold gap-2 hover:opacity-90" data-testid="add-show-btn">
                <Plus className="w-4 h-4" /> Add Show
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold" style={{ fontFamily: 'Outfit' }}>Add a Show</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {!selectedResult ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        placeholder="Search movies & TV shows..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12 pr-10"
                        autoFocus
                      />
                      {searching && (
                        <div className="absolute right-3 top-3.5 w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                      )}
                    </div>
                    {searchResults.length > 0 && (
                      <div className="max-h-64 overflow-y-auto rounded-xl border border-white/10 bg-black/40 divide-y divide-white/5">
                        {searchResults.map(result => (
                          <button key={result.tmdb_id} onClick={() => selectTMDBResult(result)} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left">
                            <div className="w-10 h-14 rounded-lg bg-white/5 overflow-hidden flex-shrink-0">
                              {result.poster_url ? <img src={result.poster_url} alt={result.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Tv className="w-4 h-4 text-neutral-600" /></div>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{result.title}</p>
                              <p className="text-xs text-neutral-500 mt-0.5">{result.media_type === 'movie' ? '🎬 Movie' : '📺 TV'} {result.year && `· ${result.year}`} {result.rating > 0 && `· ⭐ ${result.rating}`}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchQuery.length > 0 && !searching && searchResults.length === 0 && (
                      <div>
                        <div className="flex items-center gap-3 my-1">
                          <div className="flex-1 h-px bg-white/10" />
                          <span className="text-xs text-neutral-600">or enter manually</span>
                          <div className="flex-1 h-px bg-white/10" />
                        </div>
                        <Input placeholder="Show title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
                      </div>
                    )}
                    {searchQuery.length === 0 && (
                      <div>
                        <div className="flex items-center gap-3 my-1">
                          <div className="flex-1 h-px bg-white/10" />
                          <span className="text-xs text-neutral-600">or enter manually</span>
                          <div className="flex-1 h-px bg-white/10" />
                        </div>
                        <Input placeholder="Show title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-violet-500/10 border border-violet-500/20">
                      {selectedResult.poster_url && <img src={selectedResult.poster_url} alt={selectedResult.title} className="w-10 h-14 rounded-lg object-cover flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{selectedResult.title}</p>
                        <p className="text-xs text-neutral-400 mt-0.5">{selectedResult.media_type === 'movie' ? '🎬 Movie' : '📺 TV'} {selectedResult.year && `· ${selectedResult.year}`}</p>
                      </div>
                      <button onClick={clearSelection} className="p-1 text-neutral-500 hover:text-white transition-colors flex-shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                    {loadingProviders && <p className="text-xs text-neutral-500">Finding streaming services...</p>}
                    {!loadingProviders && providers.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs text-neutral-500">Available on:</p>
                        <div className="flex flex-wrap gap-2">
                          {providers.map(p => (
                            <button key={p.service_id} onClick={() => handleServiceChange(p.service_id)}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${form.service_id === p.service_id ? 'bg-violet-600 text-white' : p.available ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-white/5 text-neutral-600'}`}>
                              {p.available ? '✓ ' : ''}{SERVICE_META[p.service_id]?.name || p.service_id}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {!loadingProviders && providers.length === 0 && (
                      <p className="text-xs text-neutral-500">No streaming info found — select manually below</p>
                    )}
                  </div>
                )}
                <Select value={form.service_id} onValueChange={handleServiceChange}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-white rounded-xl h-12">
                    <SelectValue placeholder="Select streaming service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    {connectedServices.map(svcId => <SelectItem key={svcId} value={svcId}>{SERVICE_META[svcId]?.name || svcId}</SelectItem>)}
                  </SelectContent>
                </Select>
                {folders.length > 0 && (
                  <Select value={form.folder_id || 'none'} onValueChange={v => setForm({ ...form, folder_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-white rounded-xl h-12">
                      <SelectValue placeholder="Add to folder (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/10 text-white">
                      <SelectItem value="none">No folder</SelectItem>
                      {folders.map(f => <SelectItem key={f.folder_id} value={f.folder_id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button onClick={addShow} className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90">
                  Add to Watchlist
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Continue Watching row */}
      {/* Continue Watching lives on the Hub, not the Watchlist (want-to-watch only). */}

      {/* Search + filters */}
      {reorderMode && (
        <p className="text-xs text-neutral-500 text-center">
          Drag the <span className="text-neutral-400 font-semibold">≡</span> handles to reorder · tap <span className="text-violet-400 font-semibold">Done</span> when finished
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-neutral-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search your watchlist..."
            value={watchlistSearch}
            onChange={e => setWatchlistSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 rounded-xl text-sm border h-10 outline-none focus:border-violet-500/50 transition-colors ${inputBg}`}
          />
          {watchlistSearch && (
            <button onClick={() => setWatchlistSearch('')} className="absolute right-3 top-2.5 text-neutral-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <Select value={filterService} onValueChange={setFilterService}>
          <SelectTrigger className="w-36 bg-[#0A0A0A] border-white/10 text-white rounded-xl h-10 text-sm">
            <SelectValue placeholder="All Services" />
          </SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="all">All Services</SelectItem>
            {connectedServices.map(svcId => <SelectItem key={svcId} value={svcId}>{SERVICE_META[svcId]?.name || svcId}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 bg-[#0A0A0A] border-white/10 text-white rounded-xl h-10 text-sm">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={v => { setSortBy(v); }}>
          <SelectTrigger className="w-32 bg-[#0A0A0A] border-white/10 text-white rounded-xl h-10 text-sm">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="rank">Manual order</SelectItem>
            <SelectItem value="title">Title A–Z</SelectItem>
            <SelectItem value="date">Date added</SelectItem>
            <SelectItem value="watched">Last watched</SelectItem>
            <SelectItem value="rating">My rating</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {watchlistSearch && (
        <p className="text-xs text-neutral-500">
          {filteredShows.length === 0 ? 'No shows match' : `${filteredShows.length} show${filteredShows.length !== 1 ? 's' : ''} match "${watchlistSearch}"`}
        </p>
      )}

      {/* Show list */}
      {filteredShows.length === 0 ? (
        <div className="text-center py-16">
          <Tv className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          {watchlistSearch ? (
            <p className="text-neutral-500">No shows match "{watchlistSearch}"</p>
          ) : filterService !== 'all' || filterStatus !== 'all' ? (
            <p className="text-neutral-500">No shows match your filters.</p>
          ) : (
            <>
              <p className="text-neutral-400 font-semibold mb-1">Your watchlist is empty</p>
              <p className="text-neutral-600 text-sm mb-5">Start tracking what you're watching.</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDialogOpen(true)}
                  className="px-5 py-2 rounded-full text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white transition-colors"
                >
                  Search shows
                </button>
                <button
                  onClick={() => navigate('/discover')}
                  className="px-5 py-2 rounded-full text-sm font-semibold border border-white/10 text-neutral-300 hover:text-white hover:border-white/20 transition-colors"
                >
                  Browse Discover
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredShows.map((show, idx) => {
            const meta = SERVICE_META[show.service_id];
            const folder = folders.find(f => f.folder_id === show.folder_id);
            const finishRate = show.tmdb_id ? finishRates[show.tmdb_id] : undefined;
            const shield = spoilerShields[show.show_id];
            const statusBadge = getStatusBadge(show.status);

            return (
              <div
                key={show.show_id}
                data-show-index={idx}
                draggable={reorderMode}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                onTouchStart={(e) => handleTouchStart(e, idx)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`group flex flex-col rounded-xl ${cardBg} border ${cardBorder} transition-all overflow-hidden ${reorderMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                {shield?.spoiler_risk && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20">
                    <ShieldAlert className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                    <p className="text-xs text-amber-400 font-medium">{shield.message}</p>
                  </div>
                )}
                <div className="flex items-center gap-3 p-4" style={{ cursor: reorderMode ? 'grab' : 'pointer' }}
                  onClick={() => !reorderMode && handleWatchTap(show)}>

                  {reorderMode ? (
                    <div className="flex-shrink-0 p-2 -ml-1 text-neutral-400 touch-none">
                      <GripVertical className="w-5 h-5" />
                    </div>
                  ) : (
                    <span className="text-xs font-mono text-neutral-600 w-6 text-center flex-shrink-0">#{show.rank}</span>
                  )}

                  <div className="w-16 h-24 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {show.image_url ? <img src={show.image_url} alt={show.title} className="w-full h-full object-cover" /> : <Tv className="w-6 h-6 text-neutral-600" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-base font-semibold ${textPrimary}`}>{show.title}</p>
                      {episodeBadges[show.show_id] && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                          episodeBadges[show.show_id].days_until === 0 ? 'bg-red-500/20 text-red-400' :
                          episodeBadges[show.show_id].days_until > 0 ? 'bg-violet-500/20 text-violet-400' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {episodeBadges[show.show_id].days_until === 0 ? '🔴 ' : episodeBadges[show.show_id].days_until > 0 ? '🗓 ' : '✨ '}
                          {episodeBadges[show.show_id].label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {meta && <span className="text-xs text-neutral-500 flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: meta.color }} />{meta.name}</span>}
                      {folder && <span className="text-xs px-1.5 py-0.5 rounded bg-white/5 text-neutral-500">{folder.name}</span>}
                      {(show.media_type || 'tv') === 'tv' && show.status !== 'watched' && !reorderMode ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); openPositioner(show); }}
                          className={`text-xs ${Number(show.current_season) > 0 && Number(show.current_episode) > 0 ? 'text-neutral-500 hover:text-white' : 'text-[#4A9EFF] hover:text-white'} transition-colors`}
                          title="Set the episode you're on"
                        >
                          {Number(show.current_season) > 0 && Number(show.current_episode) > 0
                            ? `S${show.current_season}E${show.current_episode} ✎`
                            : '+ Set episode'}
                        </button>
                      ) : Number(show.current_season) > 0 && Number(show.current_episode) > 0 && (
                        <span className="text-xs text-neutral-600">S{show.current_season}E{show.current_episode}</span>
                      )}
                      {show.status === 'watched' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 bg-emerald-500/20 text-emerald-400">
                          Finished ✓
                        </span>
                      ) : finishRate !== undefined && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                          finishRate >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                          finishRate >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-violet-500/20 text-violet-400'
                        }`}>
                          {finishRate}% finish this
                        </span>
                      )}
                    </div>
                  </div>

                  {!reorderMode && (
                    <div className="flex flex-col items-end gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {statusBadge && (
                        <button
                          onClick={(e) => cycleStatus(e, show)}
                          disabled={updatingStatus === show.show_id}
                          className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-all hover:opacity-75 active:scale-95 ${statusBadge.classes} ${updatingStatus === show.show_id ? 'opacity-50' : ''}`}
                          title="Tap to change status"
                        >
                          {updatingStatus === show.show_id ? '...' : statusBadge.label}
                        </button>
                      )}
                      <div className="flex items-center gap-1">
                        {show.tmdb_id && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/show/${show.tmdb_id}`, { state: { media_type: show.media_type || 'tv' } }); }}
                            className="p-1.5 text-neutral-500 hover:text-violet-400 transition-colors"
                            title="View details, crossovers & more"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteShow(show.show_id); }}
                          className="p-1.5 text-neutral-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {positioning?.show.show_id === show.show_id && (
                  <div className="px-4 pb-4" onClick={e => e.stopPropagation()}>
                    <EpisodePicker positioning={positioning} onChange={setPositioning} onSave={savePosition} onCancel={() => setPositioning(null)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
